/* eslint-disable @typescript-eslint/no-unsafe-member-access */

import * as RouterSelectors from "@app/root-store/selectors/root-store.selectors";

import { ALL_VIEW_COLUMNS, ProjectViewColumn, ProjectViewKey } from "@app/project/constants/all-view-columns.constants";
import { ProjectListItem, ProjectListItemData } from "@app/project/model/project-list-item";
import type { ProjectState, ProjectStatusItem } from "@app/project/store/projects.state";
import { ApiResultState, LoadingState, initialApiObjectState } from "@app/root-store/api-result.state";
import {
    ClareStatus,
    offlineClareStatus,
    onlineClareStatus,
    unconfiguredClareStatus,
    unknownClareStatus,
} from "@app/shared/model/clare-status.model";
import type { DefaultProjectorFn, MemoizedSelectorWithProps } from "@ngrx/store";
import { createFeatureSelector, createSelector } from "@ngrx/store";

import { AuthUser } from "@app/auth/model/auth-user";
import { selectGetUser } from "@app/auth/store/selectors/auth.selectors";
import { ClareServicePlan } from "@app/project/model";
import { Project } from "@app/project/model/project";
import { ProjectView } from "@app/project/model/project-view";
import { ProjectHelperService } from "@app/project/services/project-helper.service";
import { ServicePlanUtils } from "@app/project/services/service-plan-utils";
import { initialProjectState } from "@app/project/store/projects.state";
import { TableColumnDef } from "@app/shared/model/action.model";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";
import { getServicePlansObj } from "./service-plans.selectors";
import { AppHelperService } from "@app/shared/services/app-helper.service";

export type SimpleColumns = Record<ProjectViewKey, string | TableColumnDef>;

const selectState = createFeatureSelector<ProjectState>("project");

export const getActiveProjectState = createSelector(
    selectState,
    (state: ProjectState = initialProjectState) => state.activeProject || { result: null, callState: LoadingState.init }
);

/**
 * Gets the active project.
 * Currently only used in the edit project component.
 */
export const getActiveProject = createSelector(getActiveProjectState, (activeProject: ApiResultState<Project>) => activeProject.result);

export const getActiveProjectSecuritySystem = createSelector(getActiveProject, (project: Project) => project?.securitysystem || {});
export const getActiveProjectCellularProvider = createSelector(
    getActiveProject,
    (project: Project) => project?.securitysystem?.cellularProvider || null
);

export const getProjectViewState = createSelector(
    selectState,
    (state: ProjectState = initialProjectState) => state.projectView || { result: null, callState: LoadingState.init }
);

export const getProjectViewsState = createSelector(
    selectState,
    (state: ProjectState = initialProjectState) => state.projectViews || { result: null, callState: LoadingState.init }
);

export const getProjectViewsArray = createSelector(
    getProjectViewsState,
    (projectViewState: ApiResultState<ClareDbCollection<ProjectView>>) => Object.values(projectViewState.result || {})
);

export const getActiveProjectView = createSelector(
    getProjectViewState,
    (projectViewState: ApiResultState<ProjectView>) => projectViewState.result
);

export const getSimplifiedColumns = (authUser?: AuthUser) =>
    createSelector(getActiveProjectView, (activeView: ProjectView) => {
        const cols: ProjectViewColumn[] = (activeView?.viewData || []).map(key => ALL_VIEW_COLUMNS[key]);
        return cols.reduce((total, col) => {
            // Skip col if not in project view
            const isInView = activeView?.viewData.includes(col.viewKey);
            if (!isInView) {
                return total;
            }

            // Skip col if no permissions
            const hasPermission = !col.permission || authUser.permissions.includes(col.permission);
            if (!hasPermission) {
                return total;
            }

            // Append object thing if col specifies a sort type
            const isDateCol = col.sortType === "datetime";
            if (isDateCol) {
                return {
                    ...total,
                    [col.viewKey]: {
                        displayName: col.viewHeader,
                        dataSortType: "datetime",
                    },
                };
            }

            // Else append a string
            return {
                ...total,
                [col.viewKey]: col.viewHeader,
            };
        }, {} as SimpleColumns);
    });

export const getActiveProjectCallState = createSelector(
    getActiveProjectState,
    (activeProject: ApiResultState<Project>) => activeProject.callState
);

/**
 * Gets the active project id.
 * Currently only used in the edit project component.
 */
export const getActiveProjectId = createSelector(
    RouterSelectors.findIdParam,
    getActiveProject,
    (projectId: string, activeProject: Project) => activeProject?.id || +projectId || null
);

export const getActiveProjectStatusState = createSelector(
    selectState,
    (state: ProjectState = initialProjectState) => state.activeProjectStatus || initialProjectState.activeProjectStatus
);

export const getActiveProjectStatus = createSelector(
    getActiveProjectStatusState,
    (activeProjectStatus: ApiResultState<ClareStatus>) => activeProjectStatus.result
);

export const getProjectsState = createSelector(
    selectState,
    (state: ProjectState = initialProjectState) => state.projects || { result: null, callState: LoadingState.init }
);

export const getCentralStationProjectsState = createSelector(
    selectState,
    (state: ProjectState = initialProjectState) => state.centralStationProjects || { result: null, callState: LoadingState.init }
);

export const getProjectStatusesState = createSelector(
    selectState,
    (state: ProjectState = initialProjectState) => state.projectStatuses || { result: null, callState: LoadingState.init }
);
export const getProjectConnectState = createSelector(
    selectState,
    (state: ProjectState = initialProjectState) => state.projectStatuses || { result: null, callState: LoadingState.init }
);
export const getProjectUuids = createSelector(
    getProjectStatusesState,
    (projectStatuses: ApiResultState<ClareDbCollection<{ projectId: number; uuid: string; status?: ClareStatus }>>): string[] => {
        const uuids = [];
        if (projectStatuses.result) {
            for (const project in projectStatuses.result) {
                if (projectStatuses.result[project].uuid) {
                    uuids.push(projectStatuses.result[project].uuid);
                }
            }
        }

        return uuids;
    }
);

export const getProjectsCallState = createSelector(
    getProjectsState,
    (projectState: ApiResultState<ProjectListItemData[]>) => projectState.callState
);

export const getProjectsArray = createSelector(getProjectsState, (projectState: ApiResultState<ProjectListItemData[]>) =>
    projectState.callState === LoadingState.loaded ? projectState.result : []
);

export const getProjectUsersState = (projectId: number) =>
    createSelector(selectState, (state: ProjectState = initialProjectState) => state?.users?.[projectId] || initialApiObjectState());

/**
 * Gets the uuid of the active project.
 * Currently only used in the edit project component.
 */
export const getActiveProjectUuid = createSelector(getActiveProject, project =>
    project ? ProjectHelperService.getProjectUuid(project) : undefined
);

export const getUuid = createSelector(
    RouterSelectors.selectUrlParam("uuid"),
    getActiveProjectUuid,
    (routeUuid: string, activeUuid: string) => routeUuid || activeUuid
);

export const getActiveProjectEntityState = createSelector(
    selectState,
    (state: ProjectState = initialProjectState) => state.activeProjectEntity
);

export const getSelectedEntityState = createSelector(selectState, (state: ProjectState = initialProjectState) => state.selectedSubdealer);

export enum ProjectStatus {
    ONLINE = "online",
    OFFLINE = "offline",
    UNCONFIGURED = "unconfigured",
}

export const getProjectStatusCounts = createSelector(getProjectStatusesState, projectStatuses => {
    let online = 0;
    let offline = 0;
    let unconfigured = 0;
    for (const id in projectStatuses.result) {
        if (projectStatuses.result[id]) {
            switch (projectStatuses.result[id].status) {
                case onlineClareStatus:
                    online++;
                    break;

                case offlineClareStatus:
                    offline++;
                    break;

                default: {
                    unconfigured++;
                }
            }
        }
    }

    const result: Record<ProjectStatus, number> = { online, offline, unconfigured };
    return result;
});

export const getProjectActiveCounts = createSelector(getProjectsArray, (projects: any) => {
    let active = 0;
    let suspended = 0;
    if (projects) {
        projects.forEach(project => {
            if (project.inactive) {
                suspended += 1;
            } else {
                active += 1;
            }
        });
    }
    return { active, suspended };
});

export const getProjectStatus = (projectId: number) =>
    createSelector(getProjectStatusesState, projectStatuses =>
        projectStatuses.result && projectStatuses.result[projectId] ? projectStatuses.result[projectId].status : unknownClareStatus
    );

const getViewArchivePermission = createSelector(selectGetUser, authUser => authUser?.permissions?.indexOf("viewArchivedProjects") >= 0);

export type ClareServicePlansList = Record<number, ClareServicePlan>;
const getServicePlanList = createSelector(getServicePlansObj, (servicePlansObj: ClareServicePlansList) => {
    const servicePlans: Record<number, any> = { ...servicePlansObj };
    // probably want to change the color of inactive plans to gray or something
    for (const key in servicePlans) {
        if (servicePlans[key]) {
            servicePlans[key] = [servicePlans[key].name, ServicePlanUtils.smallIconColor(servicePlans[key])];
        }
    }

    return servicePlans || {};
});

type ProjectStatuses = ApiResultState<ClareDbCollection<ProjectStatusItem>>;
const getProjectListData = createSelector(
    getProjectsArray,
    getActiveProjectView,
    getProjectStatusesState,
    getServicePlanList,
    (
        projects: ProjectListItemData[],
        activeProjectView: ProjectView,
        projectStatuses: ProjectStatuses,
        servicePlans: Record<number, any>
    ) => {
        const cols: ProjectViewColumn[] = (activeProjectView?.viewData || []).map(key => ALL_VIEW_COLUMNS[key]);
        const buildViewData = (d: ProjectListItemData) =>
            cols.reduce((total, col) => {
                total[col.viewKey] = col.getViewData(d);
                return total;
            }, {});
        return (projects || [])?.map(
            (project): ProjectListItem => ({
                ...buildViewData(project),
                isOccupied: project.isOccupied,
                id: project.id,
                inactive: project.inactive,
                monitoredStatus: getMonitoredStatus(servicePlans, project.servicePlanId),
                onlineStatus: getOnlineStatusOfProject(projectStatuses.result, project.id),
                connectionType: getConnectionTypeOfProject(projectStatuses.result, project.id),
                dbData: { ...project },
                entityId: project.entityId,
                lockoutTime: project.lockoutTime ? AppHelperService.getLocalDate(project.lockoutTime) : "",
            })
        );
    }
);

const getOnlineStatusOfProject = (projectStatusItems: ClareDbCollection<ProjectStatusItem>, projectId: number): ClareStatus =>
    projectStatusItems?.[projectId]?.status || unconfiguredClareStatus;

const getMonitoredStatus = (servicePlans: Record<number, any>, servicePlanId: number): string[] => {
    if (!servicePlans) {
        return [];
    }
    const selectedServicePlan: string[] = servicePlans[servicePlanId] as string[] | undefined;
    if (selectedServicePlan?.length > 0) {
        return selectedServicePlan;
    }
    return Object.values<string[]>(servicePlans).find(([planName]): boolean => planName === "Basic");
};
const getConnectionTypeOfProject = (projectStatusItems: ClareDbCollection<ProjectStatusItem>, projectId: number): any =>
    projectStatusItems?.[projectId]?.connectionType || "";

export const getFilteredProjectListData: MemoizedSelectorWithProps<
    Record<PropertyKey, unknown>,
    unknown,
    ProjectListItem[],
    DefaultProjectorFn<ProjectListItem[]>
> = createSelector(getProjectListData, getViewArchivePermission, (projects: ProjectListItem[], includeSuspended: boolean) =>
    projects.filter(project => (includeSuspended && project.dbData.inactive) || !project.dbData.inactive)
);

export const getProjectServicePlanCount = () =>
    createSelector(getProjectListData, (projects: any[]) => {
        let basic = 0;
        let interactive = 0;
        let interactivePlus = 0;
        let interactivePlusLTE = 0;
        projects?.forEach(project => {
            if (project.monitoredStatus) {
                if (project.monitoredStatus[0] === "Basic") {
                    basic += 1;
                    return;
                }

                if (project.monitoredStatus[0] === "Interactive") {
                    interactive += 1;
                    return;
                }

                if (project.monitoredStatus[0] === "Interactive Plus") {
                    interactivePlus += 1;
                    return;
                }

                if (project.monitoredStatus[0] === "Interactive Plus LTE") {
                    interactivePlusLTE += 1;
                }
            }
        });

        return { Basic: basic, Interactive: interactive, "Interactive Plus": interactivePlus, "Interactive Plus LTE": interactivePlusLTE };
    });

export const getActiveProjectServicePlanCount = () =>
    createSelector(getProjectListData, (projects: any[]) => {
        let basic = 0;
        let interactive = 0;
        let interactivePlus = 0;
        let interactivePlusLTE = 0;
        projects?.forEach(project => {
            if (project.monitoredStatus && !project.inactive) {
                if (project.monitoredStatus[0] === "Basic") {
                    basic += 1;
                    return;
                }

                if (project.monitoredStatus[0] === "Interactive") {
                    interactive += 1;
                    return;
                }

                if (project.monitoredStatus[0] === "Interactive Plus") {
                    interactivePlus += 1;
                    return;
                }

                if (project.monitoredStatus[0] === "Interactive Plus LTE") {
                    interactivePlusLTE += 1;
                }
            }
        });

        return { Basic: basic, Interactive: interactive, "Interactive Plus": interactivePlus, "Interactive Plus LTE": interactivePlusLTE };
    });

export const selectProjectOccupancyCounts = () =>
    createSelector(getProjectListData, (projects: any[]) => {
        let isOccupied = 0;
        let unOccupied = 0;
        if (projects) {
            projects.forEach(project => {
                if (project.isOccupied) {
                    isOccupied += 1;
                } else {
                    unOccupied += 1;
                }
            });
        }
        return { isOccupied, unOccupied };
    });

export const selectActiveProjectOccupancyCounts = () =>
    createSelector(getProjectListData, (projects: any[]) => {
        let isOccupied = 0;
        let unOccupied = 0;
        if (projects) {
            projects.forEach(project => {
                if (!project.inactive) {
                    if (project.isOccupied) {
                        isOccupied += 1;
                    } else {
                        unOccupied += 1;
                    }
                }
            });
        }
        return { isOccupied, unOccupied };
    });

export const selectProjectLockoutCount = () =>
    createSelector(getProjectListData, (projects: any[]) => {
        let isLockedOut = 0;
        if (projects) {
            projects.forEach(project => {
                if (project.lockoutTime?.length) {
                    isLockedOut += 1;
                }
            });
        }
        return isLockedOut;
    });

export const selectActiveProjectLockoutCount = () =>
    createSelector(getProjectListData, (projects: any[]) => {
        let isLockedOut = 0;
        if (projects) {
            projects.forEach(project => {
                if (project.lockoutTime?.length && !project.inactive) {
                    isLockedOut += 1;
                }
            });
        }
        return isLockedOut;
    });

export const getActiveProjectFeatures = () => createSelector(selectState, state => state.activeProjectFeatures);

export const getAllAdditionalFeatures = () => createSelector(selectState, state => state.allAdditionalFeatures);

export const getActivePanelSummaryState = () => createSelector(selectState, state => state.activePanelSummary);

export const getActiveProjectStatusCounts = createSelector(getProjectListData, projects => {
    let online = 0;
    let offline = 0;
    let unconfigured = 0;
    projects.forEach(project => {
        if (!project.inactive) {
            switch (project.onlineStatus) {
                case onlineClareStatus:
                    online += 1;
                    break;

                case offlineClareStatus:
                    offline += 1;
                    break;

                default: {
                    unconfigured += 1;
                }
            }
        }
    });

    const result: Record<ProjectStatus, number> = { online, offline, unconfigured };
    return result;
});
